/**
 * Secure Admin API Service
 * Handles all admin API calls with multiple security layers
 */

import axios from 'axios';
import API_CONFIG from '../config/api';

class AdminApiService {
  constructor() {
    this.baseURL = API_CONFIG.getApiUrl();
    this.requestCount = 0;
    this.requestTimestamps = [];
    this.maxRequestsPerMinute = 30; // Rate limiting
    this.sessionTimeout = 15 * 60 * 1000; // 15 minutes
    this.lastActivity = Date.now();
    
    // Create axios instance with security headers
    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest' // CSRF protection
      }
    });
    
    // Request interceptor - Add auth and rate limiting
    this.client.interceptors.request.use(
      (config) => {
        // Check session timeout
        if (Date.now() - this.lastActivity > this.sessionTimeout) {
          this.handleSessionTimeout();
          return Promise.reject(new Error('Session expired'));
        }
        
        // Rate limiting check
        if (!this.checkRateLimit()) {
          return Promise.reject(new Error('Rate limit exceeded. Please wait.'));
        }
        
        // Update last activity
        this.lastActivity = Date.now();
        
        // Get token from secure storage
        const token = this.getSecureToken();
        if (!token) {
          this.handleUnauthorized();
          return Promise.reject(new Error('Not authenticated'));
        }
        
        // Add auth header
        config.headers['Authorization'] = `Bearer ${token}`;
        
        // Add request ID for tracking
        config.headers['X-Request-ID'] = this.generateRequestId();
        
        // Add timestamp for audit
        config.metadata = {
          timestamp: new Date().toISOString(),
          endpoint: config.url,
          method: config.method
        };
        
        return config;
      },
      (error) => {
        this.logSecurityEvent('REQUEST_ERROR', { error: error.message });
        return Promise.reject(error);
      }
    );
    
    // Response interceptor - Handle errors and audit
    this.client.interceptors.response.use(
      (response) => {
        // Log successful admin action
        if (response.config.url?.includes('/admin/')) {
          this.logAdminAction('SUCCESS', response.config);
        }
        return response;
      },
      (error) => {
        // Handle errors
        if (error.response?.status === 401) {
          this.handleUnauthorized();
        } else if (error.response?.status === 403) {
          this.logSecurityEvent('UNAUTHORIZED_ACCESS', {
            endpoint: error.config?.url,
            method: error.config?.method
          });
        } else if (error.response?.status === 429) {
          this.logSecurityEvent('RATE_LIMIT_EXCEEDED', {
            endpoint: error.config?.url
          });
        }
        
        // Log admin action failure
        if (error.config?.url?.includes('/admin/')) {
          this.logAdminAction('FAILURE', error.config, error.response?.data);
        }
        
        return Promise.reject(error);
      }
    );
    
    // Setup session monitoring
    this.setupSessionMonitoring();
  }
  
  // Secure token storage (consider using httpOnly cookies in production)
  getSecureToken() {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return null;
      
      // Validate token format (basic check)
      if (token.length < 20) {
        this.clearAuth();
        return null;
      }
      
      return token;
    } catch (e) {
      console.error('Error getting token:', e);
      return null;
    }
  }
  
  // Rate limiting
  checkRateLimit() {
    const now = Date.now();
    const oneMinuteAgo = now - 60000;
    
    // Remove old timestamps
    this.requestTimestamps = this.requestTimestamps.filter(t => t > oneMinuteAgo);
    
    // Check limit
    if (this.requestTimestamps.length >= this.maxRequestsPerMinute) {
      return false;
    }
    
    // Add current request
    this.requestTimestamps.push(now);
    this.requestCount++;
    
    return true;
  }
  
  // Session monitoring
  setupSessionMonitoring() {
    // Monitor user activity
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => {
      document.addEventListener(event, () => {
        this.lastActivity = Date.now();
      }, { passive: true });
    });
    
    // Check session periodically
    setInterval(() => {
      if (Date.now() - this.lastActivity > this.sessionTimeout) {
        this.handleSessionTimeout();
      }
    }, 60000); // Check every minute
  }
  
  // Handle session timeout
  handleSessionTimeout() {
    this.logSecurityEvent('SESSION_TIMEOUT', {});
    this.clearAuth();
    window.location.href = '/login?timeout=1';
  }
  
  // Handle unauthorized
  handleUnauthorized() {
    this.logSecurityEvent('UNAUTHORIZED_ACCESS', {});
    this.clearAuth();
    window.location.href = '/login?unauthorized=1';
  }
  
  // Clear auth
  clearAuth() {
    localStorage.removeItem('authToken');
    localStorage.removeItem('adminSession');
    delete axios.defaults.headers.common['Authorization'];
  }
  
  // Generate request ID
  generateRequestId() {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
  
  // Log admin actions for audit
  logAdminAction(status, config, error = null) {
    try {
      const action = {
        status,
        endpoint: config.url,
        method: config.method,
        timestamp: config.metadata?.timestamp || new Date().toISOString(),
        requestId: config.headers['X-Request-ID'],
        error: error ? JSON.stringify(error) : null
      };
      
      // Store in session storage for audit trail
      const auditLog = JSON.parse(sessionStorage.getItem('adminAuditLog') || '[]');
      auditLog.push(action);
      
      // Keep only last 100 actions
      if (auditLog.length > 100) {
        auditLog.shift();
      }
      
      sessionStorage.setItem('adminAuditLog', JSON.stringify(auditLog));
      
      // Also send to backend for SIEM (non-blocking)
      this.sendAuditToBackend(action).catch(() => {
        // Silently fail - audit is logged locally
      });
    } catch (e) {
      console.error('Error logging admin action:', e);
    }
  }
  
  // Send audit to backend
  async sendAuditToBackend(action) {
    try {
      await axios.post(`${this.baseURL}/admin/issues/audit`, action, {
        headers: {
          'Authorization': `Bearer ${this.getSecureToken()}`
        }
      });
    } catch (e) {
      // Silently fail - audit is logged locally
    }
  }
  
  // Log security events
  logSecurityEvent(eventType, details) {
    console.warn(`[SECURITY] ${eventType}:`, details);
    
    // Store in session storage
    const securityEvents = JSON.parse(sessionStorage.getItem('securityEvents') || '[]');
    securityEvents.push({
      type: eventType,
      details,
      timestamp: new Date().toISOString()
    });
    
    if (securityEvents.length > 50) {
      securityEvents.shift();
    }
    
    sessionStorage.setItem('securityEvents', JSON.stringify(securityEvents));
  }
  
  // Sanitize input to prevent XSS
  sanitizeInput(input) {
    if (typeof input !== 'string') return input;
    
    const div = document.createElement('div');
    div.textContent = input;
    return div.innerHTML;
  }
  
  // Validate admin access
  async validateAdminAccess() {
    try {
      const response = await this.client.get('/auth/validate');
      const user = response.data.user || response.data;
      
      // Check if user is admin
      if (!user.is_admin && !(user.roles || []).includes('admin')) {
        throw new Error('Admin access required');
      }
      
      return { valid: true, user };
    } catch (error) {
      this.handleUnauthorized();
      return { valid: false, error: error.message };
    }
  }
  
  // Admin Dashboard
  async getDashboard() {
    return await this.client.get('/admin/issues/dashboard');
  }
  
  // List Issues
  async listIssues(filters = {}) {
    const params = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        params.append(key, filters[key]);
      }
    });
    
    return await this.client.get(`/admin/issues/issues?${params.toString()}`);
  }
  
  // Get Issue Details
  async getIssueDetails(issueId) {
    return await this.client.get(`/admin/issues/issues/${issueId}`);
  }
  
  // Update Issue
  async updateIssue(issueId, updates) {
    // Sanitize inputs
    const sanitized = {};
    Object.keys(updates).forEach(key => {
      if (typeof updates[key] === 'string') {
        sanitized[key] = this.sanitizeInput(updates[key]);
      } else {
        sanitized[key] = updates[key];
      }
    });
    
    return await this.client.put(`/admin/issues/issues/${issueId}/update`, sanitized);
  }
  
  // Assign Issue
  async assignIssue(issueId, assignedToUserId) {
    return await this.client.post(`/admin/issues/issues/${issueId}/assign`, {
      assigned_to_user_id: assignedToUserId
    });
  }
  
  // Resolve Issue
  async resolveIssue(issueId, resolution) {
    // Sanitize resolution text
    const sanitized = {
      resolution: this.sanitizeInput(resolution.resolution || ''),
      resolution_steps: (resolution.resolution_steps || []).map(step => this.sanitizeInput(step)),
      security_notes: this.sanitizeInput(resolution.security_notes || '')
    };
    
    return await this.client.post(`/admin/issues/issues/${issueId}/resolve`, sanitized);
  }
  
  // Add Comment
  async addComment(issueId, comment, isInternal = false) {
    return await this.client.post(`/admin/issues/issues/${issueId}/comment`, {
      content: this.sanitizeInput(comment),
      is_internal: isInternal
    });
  }
  
  // Get User Issues
  async getUserIssues(userId) {
    return await this.client.get(`/admin/issues/users/${userId}/issues`);
  }
  
  // Create Support Issue
  async createSupportIssue(userId, issueData) {
    const sanitized = {
      title: this.sanitizeInput(issueData.title || ''),
      description: this.sanitizeInput(issueData.description || ''),
      category: issueData.category,
      priority: issueData.priority
    };
    
    return await this.client.post(`/admin/issues/users/${userId}/help`, sanitized);
  }
  
  // Get Analytics
  async getAnalytics() {
    return await this.client.get('/admin/issues/analytics');
  }
}

// Export singleton instance
export default new AdminApiService();
